<template>
  <div class="flex items-center space-x-4">
    <template v-if="enableQuickSelect">
      <select
        v-model="selectedOption"
        class="px-2 py-1 bg-white border rounded text-slate-700 focus:outline-none focus:ring focus:border-blue-30 h-[44px]"
      >
        <option value="this_month">This Month</option>
        <option value="next_1_months">Next 1 Month</option>
        <option value="next_2_months">Next 2 Months</option>
        <option value="next_3_months">Next 3 Months</option>
        <option value="next_6_months">Next 6 Months</option>
        <option value="next_12_months">Next 12 Months</option>
        <option value="custom">Custom</option>
      </select>
    </template>
    <VueDatePicker
      :range="true"
      :format="'dd/MM/yyyy'"
      :enable-time-picker="false"
      :multi-calendars="2"
      :auto-apply="true"
      v-model="dateRange"
      @update:modelValue="onDateChange"
      class="flex-1 vue_picker_custom_style"
    />
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import moment from 'moment';

const props = defineProps({
  modelValue: {
    type: [Array, Object],
    default: () => []
  },
  enableQuickSelect: {
    type: Boolean,
    default: true
  }
});

const enableQuickSelect = computed(() => props.enableQuickSelect);
const emit = defineEmits(['update:modelValue', 'change']);

const selectedOption = ref('custom');
const dateRange = ref(props.modelValue && props.modelValue.length ? [...props.modelValue] : []);

// Helper to get start/end of month/quarter
function getRange(option) {
  const today = moment();
  if (option === 'this_month') {
    return [today.clone().startOf('month').toDate(), today.clone().endOf('month').toDate()];
  } else if (option === 'next_1_months') {
    return [today.clone().toDate(), today.clone().add(1, 'month').toDate()];
  } else if (option === 'next_2_months') {
    return [today.clone().toDate(), today.clone().add(2, 'month').toDate()];
  } else if (option === 'next_3_months') {
    return [today.clone().toDate(), today.clone().add(3, 'month').toDate()];
  } else if (option === 'next_6_months') {
    return [today.clone().toDate(), today.clone().add(6, 'month').toDate()];
  } else if (option === 'next_12_months') {
    return [today.clone().toDate(), today.clone().add(12, 'month').toDate()];
  } else if (option === 'next_month') {
    const next = today.clone().add(1, 'month');
    return [next.clone().startOf('month').toDate(), next.clone().endOf('month').toDate()];
  } else if (option === 'next_quarter') {
    const nextQ = today.clone().add(1, 'quarter');
    return [nextQ.clone().startOf('quarter').toDate(), nextQ.clone().endOf('quarter').toDate()];
  }
  return [];
}

// Watch dropdown and update datepicker
watch(selectedOption, (val) => {
  if (val !== 'custom') {
    dateRange.value = getRange(val);
    emit('update:modelValue', dateRange.value);
    emit('change', dateRange.value);
  }
});

// Watch datepicker and update dropdown
function onDateChange(val) {
  dateRange.value = val;
  emit('update:modelValue', val);
  emit('change', val);
  // If not matching any preset, set to custom
  if (!isPreset(val)) {
    selectedOption.value = 'custom';
  }
}

function isPreset(val) {
  if (!val || val.length !== 2) return false;
  const thisMonth = getRange('this_month');
  const nextMonth = getRange('next_month');
  const nextQuarter = getRange('next_quarter');
  const same = (a, b) => a[0].toDateString() === b[0].toDateString() && a[1].toDateString() === b[1].toDateString();
  return (
    same(val, thisMonth) ||
    same(val, nextMonth) ||
    same(val, nextQuarter)
  );
}

// Sync with parent
watch(() => props.modelValue, (val) => {
  if (val && val.length === 2) {
    dateRange.value = [...val];
    if (!isPreset(val)) {
      selectedOption.value = 'custom';
    }
  }
});
</script>

<style>
.vue_picker_custom_style input {
  height: 44px;
}
</style>